import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CContainer, CSpinner } from "@coreui/react-pro";
import PublicContentPageShell from "../components/public/PublicContentPageShell";
import config from "../config/config";
import "../styles/public-experience.css";
import "../styles/public-news.css";

function formatDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "full",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (_error) {
    return "";
  }
}

function paragraphize(value) {
  return String(value || "")
    .split(/\r?\n\r?\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);
}

export default function NachrichtenArtikel() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    async function loadArticle() {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${config.API_BASE_URL}/api/news/public/${slug}`, {
          signal: controller.signal,
          credentials: "include",
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || "Artikel konnte nicht geladen werden.");
        }
        if (ignore) return;
        setArticle(payload);
        document.title = `${payload.title} | Nachrichten | Experts4Europe`;
      } catch (loadError) {
        if (ignore || loadError.name === "AbortError") return;
        setError(loadError.message || "Artikel konnte nicht geladen werden.");
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    loadArticle();
    return () => {
      ignore = true;
      controller.abort();
    };
  }, [slug]);

  useEffect(() => {
    if (!article?.slug) return undefined;
    fetch(`${config.API_BASE_URL}/api/news/public/${article.slug}/view`, {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
    return undefined;
  }, [article?.slug]);

  const hero = useMemo(() => {
    const title = article?.title || "Nachrichtenartikel";
    const lead = article?.excerpt || "Redaktionelle Einordnung mit nachvollziehbarer Quellenlage.";

    return (
      <section className="public-news-hero public-news-hero--article">
        <CContainer fluid className="public-core-container public-page-shell public-news-shell public-news-shell--hero public-news-shell--stage">
          <div className="public-news-stage public-news-stage--hero">
            <div className="public-news-hero__panel public-page-card">
              <div className="public-news-hero__copy">
                <span className="public-page-overline">Nachrichten</span>
                <h1 className="public-page-title">{title}</h1>
                <p className="public-page-body public-news-hero__lead">{lead}</p>
              </div>

              <aside className="public-news-hero__meta">
                <article className="public-page-callout public-news-hero__meta-card">
                  <strong>{article?.category || "Alle"}</strong>
                  <span>{article?.publishedAt ? formatDate(article.publishedAt) : "Entwurf ohne Veröffentlichungsdatum"}</span>
                </article>
                <article className="public-page-callout public-news-hero__meta-card">
                  <strong>{article?.sourceName || "Redaktion"}</strong>
                  <span>{article?.viewCount || 0} Aufrufe</span>
                </article>
              </aside>
            </div>
          </div>
        </CContainer>
      </section>
    );
  }, [article]);

  return (
    <PublicContentPageShell pageClassName="public-news-page" hero={hero}>
      <section className="public-core-band public-core-band--light">
        <CContainer fluid className="public-core-container public-page-shell public-news-shell public-news-shell--detail">
          <div className="public-news-detail__nav">
            <Link to="/nachrichten">&lt; zurück zu den Nachrichten</Link>
          </div>

          {loading ? (
            <div className="public-news-state public-page-card">
              <CSpinner size="sm" />
              <span>Artikel wird geladen…</span>
            </div>
          ) : null}

          {error ? (
            <div className="public-news-state public-news-state--error public-page-card">
              <strong>Hinweis</strong>
              <span>{error}</span>
            </div>
          ) : null}

          {!loading && !error && article ? (
            <article className="public-news-detail public-page-card">
              <div className="public-news-detail__meta">
                <span>{article.category || "Alle"}</span>
                {article.sourceDomain ? <span>{article.sourceDomain}</span> : null}
                {article.searchTerm ? <span>Suchbegriff: {article.searchTerm}</span> : null}
              </div>

              <div className="public-news-detail__body">
                {paragraphize(article.body || article.excerpt).map((paragraph, index) => (
                  <p key={`${article.slug}-${index}`}>{paragraph}</p>
                ))}
              </div>

              {Array.isArray(article.sourceList) && article.sourceList.length ? (
                <div className="public-news-detail__sources">
                  <span className="public-page-overline">Quellen</span>
                  <ul>
                    {article.sourceList.map((source, index) => (
                      <li key={`${source.url || source.title || index}-${index}`}>
                        <strong>{source.title || source.sourceName || `Quelle ${index + 1}`}</strong>
                        {source.sourceName ? <span>{source.sourceName}</span> : null}
                        {source.url ? (
                          <a href={source.url} target="_blank" rel="noreferrer">
                            {source.url}
                          </a>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </article>
          ) : null}
        </CContainer>
      </section>
    </PublicContentPageShell>
  );
}
